import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { AnteriorMipResult, mipPointToPx } from './volumeMip';

/**
 * Where the currently displayed viewing plane sits on the series overview.
 *
 * The overview is a parallel anterior projection, which collapses the
 * viewer-depth axis. The indicator is always a single line — the overview
 * stays a plain 2D scout — but the line is only an EXACT statement when the
 * plane contains the projection direction (an aligned transverse plane, a
 * rolled one, a sagittal one). For a tilted plane the full footprint would
 * be a band, and a band is not a usable position marker; instead the line
 * drawn is the plane's cut at the CROSSHAIR'S depth — a true "the plane
 * passes here at the current point" — and `exact: false` tells the caller
 * to present it as approximate (dashed/dimmed, with a note) rather than as
 * the exact marker.
 *
 * - `line`: endpoints in overview pixel space, clipped to the volume.
 *   `exact` is true when the line IS the plane's entire footprint; for an
 *   exact plane the crosshair cut and the footprint coincide, so the same
 *   definition serves both.
 * - `parallel`: the plane is (near-)parallel to the overview — it has no
 *   defined cut direction, and no line can be truthfully drawn. The caller
 *   must SAY so; silence already means "outside the volume".
 * - `outside`: the plane's cut at the crosshair depth misses the volume
 *   (the crosshair is outside it, or the plane is) — nothing to draw, same
 *   posture as the out-of-volume crosshair.
 */
export type MipPlaneIndicator =
    | {
          kind: 'line';
          fromPx: readonly [number, number];
          toPx: readonly [number, number];
          exact: boolean;
      }
    | { kind: 'parallel' }
    | { kind: 'outside' };

/**
 * Below this mean thickness (2·area/perimeter, in overview px) the plane's
 * full footprint on the overview is indistinguishable from a line and the
 * line is reported as exact — which also absorbs numerical noise in the
 * exactly-aligned case, and keeps a fraction-of-a-degree tilt from demoting
 * the everyday marker to "approximate" over a sub-pixel band.
 */
const EXACT_MEAN_THICKNESS_PX = 0.75;

/**
 * The cut direction is the cross product of the plane normal and the depth
 * axis; below this fraction of the normal's own length it is numerically
 * meaningless and the plane is reported as parallel to the overview.
 */
const PARALLEL_CROSS_EPSILON = 1e-6;

/** Intersections closer than this (voxels) are the same corner touched twice. */
const DUPLICATE_EPSILON = 1e-6;

/**
 * Convert a plane normal from the grid frame (mm) to voxel space, for
 * {@link planeIndicatorOnMip}. Normals are covectors: with
 * x_mm = diag(spacing)·x_voxel, the plane equation n_mm·x_mm = c becomes
 * (diag(spacing)·n_mm)·x_voxel = c — each component scales by its axis'
 * spacing. Transforming the normal like a point instead would tilt the plane
 * on anisotropic voxels, which is why this lives here and not in callers.
 */
export function gridNormalToVoxelCovector(
    normalGridMm: ReadonlyVec3,
    spacingMm: readonly [number, number, number]
): vec3 {
    return vec3.fromValues(
        normalGridMm[0] * spacingMm[0],
        normalGridMm[1] * spacingMm[1],
        normalGridMm[2] * spacingMm[2]
    );
}

/**
 * Locate the displayed viewing plane on the overview.
 *
 * Everything is in VOXEL space: `planePointVoxel` (the crosshair — the
 * viewport's plane intersection point) as `Grid.toVoxelSpace` returns it,
 * and `planeNormalVoxel` as the world normal rotated into the grid frame and
 * scaled per-axis by the voxel spacing (normals are covectors: for
 * x_mm = diag(res)·x_voxel, n_voxel = diag(res)·n_mm — using the unscaled
 * normal would tilt the plane on anisotropic voxels).
 */
export function planeIndicatorOnMip(
    mip: AnteriorMipResult,
    sizeVoxels: readonly [number, number, number],
    planePointVoxel: ReadonlyVec3,
    planeNormalVoxel: ReadonlyVec3
): MipPlaneIndicator {
    const [nx, ny, nz] = [
        planeNormalVoxel[0],
        planeNormalVoxel[1],
        planeNormalVoxel[2],
    ];
    if (!Number.isFinite(nx + ny + nz) || (nx === 0 && ny === 0 && nz === 0)) {
        return { kind: 'outside' };
    }

    // The line to draw: the plane's cut at the crosshair's depth. Its
    // direction is perpendicular to the normal AND depth-free — the cross
    // product of the two. When the normal (near-)parallels the depth axis
    // the cut is undefined: the plane faces the overview.
    const normalLength = Math.hypot(nx, ny, nz);
    const depthAxisUnit: [number, number, number] = [0, 0, 0];
    depthAxisUnit[mip.depthAxis] = 1;
    const direction = cross([nx, ny, nz], depthAxisUnit);
    if (
        Math.hypot(direction[0], direction[1], direction[2]) <
        PARALLEL_CROSS_EPSILON * normalLength
    ) {
        return { kind: 'parallel' };
    }

    // Clip the line point + t·direction to the volume box (slab test per
    // axis). The depth component of `direction` is zero, so the depth slab
    // degenerates to "is the crosshair's depth inside the volume".
    let tMin = -Infinity;
    let tMax = Infinity;
    for (const axis of [0, 1, 2] as const) {
        const origin = planePointVoxel[axis];
        const step = direction[axis];
        if (step === 0) {
            if (origin < 0 || origin > sizeVoxels[axis]) {
                return { kind: 'outside' };
            }
            continue;
        }
        const tA = (0 - origin) / step;
        const tB = (sizeVoxels[axis] - origin) / step;
        tMin = Math.max(tMin, Math.min(tA, tB));
        tMax = Math.min(tMax, Math.max(tA, tB));
    }
    if (!(tMin < tMax)) {
        return { kind: 'outside' };
    }

    const endpoint = (t: number): [number, number] =>
        mipPointToPx(mip, [
            planePointVoxel[0] + t * direction[0],
            planePointVoxel[1] + t * direction[1],
            planePointVoxel[2] + t * direction[2],
        ]);

    return line(
        endpoint(tMin),
        endpoint(tMax),
        isFootprintALine(mip, sizeVoxels, planePointVoxel, [nx, ny, nz])
    );
}

/**
 * True when the plane's FULL footprint on the overview is (within a
 * sub-pixel) the line itself — i.e. the plane contains the projection
 * direction. Computed from the plane∩box polygon so the threshold is in
 * overview pixels, robust to volume size and anisotropy.
 */
function isFootprintALine(
    mip: AnteriorMipResult,
    sizeVoxels: readonly [number, number, number],
    planePointVoxel: ReadonlyVec3,
    normal: readonly [number, number, number]
): boolean {
    const [nx, ny, nz] = normal;
    const d = (x: number, y: number, z: number): number =>
        nx * (x - planePointVoxel[0]) +
        ny * (y - planePointVoxel[1]) +
        nz * (z - planePointVoxel[2]);

    const [sx, sy, sz] = sizeVoxels;
    const corners: Array<[number, number, number]> = [];
    for (const x of [0, sx]) {
        for (const y of [0, sy]) {
            for (const z of [0, sz]) {
                corners.push([x, y, z]);
            }
        }
    }
    // The 12 box edges as corner-index pairs (corners are ordered zyx-nested).
    const edges: Array<[number, number]> = [
        [0, 1],
        [2, 3],
        [4, 5],
        [6, 7], // along z
        [0, 2],
        [1, 3],
        [4, 6],
        [5, 7], // along y
        [0, 4],
        [1, 5],
        [2, 6],
        [3, 7], // along x
    ];

    const points: Array<[number, number, number]> = [];
    for (const [a, b] of edges) {
        const [ax, ay, az] = corners[a];
        const [bx, by, bz] = corners[b];
        const da = d(ax, ay, az);
        const db = d(bx, by, bz);
        if (da === 0) {
            pushUnique(points, [ax, ay, az]);
        }
        if (db === 0) {
            pushUnique(points, [bx, by, bz]);
        }
        if ((da < 0 && db > 0) || (da > 0 && db < 0)) {
            const t = da / (da - db);
            pushUnique(points, [
                ax + t * (bx - ax),
                ay + t * (by - ay),
                az + t * (bz - az),
            ]);
        }
    }
    if (points.length < 3) {
        return true;
    }

    // Project the footprint and measure its mean thickness. An angular sort
    // around the centroid orders the convex footprint; a degenerate one
    // (the exact case) has near-zero area under any ordering.
    const projected: Array<[number, number]> = points.map((point) =>
        mipPointToPx(mip, point)
    );
    const centroid: [number, number] = [
        projected.reduce((sum, p) => sum + p[0], 0) / projected.length,
        projected.reduce((sum, p) => sum + p[1], 0) / projected.length,
    ];
    const ordered = [...projected].sort(
        (a, b) =>
            Math.atan2(a[1] - centroid[1], a[0] - centroid[0]) -
            Math.atan2(b[1] - centroid[1], b[0] - centroid[0])
    );
    let area = 0;
    let perimeter = 0;
    for (let i = 0; i < ordered.length; i++) {
        const [x1, y1] = ordered[i];
        const [x2, y2] = ordered[(i + 1) % ordered.length];
        area += x1 * y2 - x2 * y1;
        perimeter += Math.hypot(x2 - x1, y2 - y1);
    }
    area = Math.abs(area) / 2;
    const meanThickness = perimeter > 0 ? (2 * area) / perimeter : 0;
    return meanThickness <= EXACT_MEAN_THICKNESS_PX;
}

/** Endpoints ordered left-to-right (top-to-bottom on ties) so the result is
 * independent of clip order. */
function line(
    a: readonly [number, number],
    b: readonly [number, number],
    exact: boolean
): MipPlaneIndicator {
    const flipped = a[0] > b[0] || (a[0] === b[0] && a[1] > b[1]);
    const [from, to] = flipped ? [b, a] : [a, b];
    return { kind: 'line', fromPx: from, toPx: to, exact };
}

function cross(
    a: readonly [number, number, number],
    b: readonly [number, number, number]
): [number, number, number] {
    return [
        a[1] * b[2] - a[2] * b[1],
        a[2] * b[0] - a[0] * b[2],
        a[0] * b[1] - a[1] * b[0],
    ];
}

function pushUnique(
    points: Array<[number, number, number]>,
    candidate: [number, number, number]
): void {
    const exists = points.some(
        (point) =>
            Math.abs(point[0] - candidate[0]) < DUPLICATE_EPSILON &&
            Math.abs(point[1] - candidate[1]) < DUPLICATE_EPSILON &&
            Math.abs(point[2] - candidate[2]) < DUPLICATE_EPSILON
    );
    if (!exists) {
        points.push(candidate);
    }
}
